// ═══════════════════════════════════════════════════════════════
// phone.ts – telefonszám (és fax) ellenőrzése és magyar formázási javaslat
//
// Elfogadott: számjegy, szóköz és a szokásos tagolójelek: + - – / ( ) .
//   - a „+” csak a szám elején állhat;
//   - egy számban 6–15 számjegy lehet (E.164: legfeljebb 15);
//   - legfeljebb 3 szám, vesszővel vagy pontosvesszővel elválasztva;
//   - a végén mellékállomás: „mellék 123”, „m. 123”, „ext 123”;
//   - üres érték, illetve önmagában „-” vagy „–” (nincs szám) megengedett.
// Betű vagy más szöveg nem kerülhet bele.
//
// Figyelmeztetés (nem hiba): ha a szám magyar (+36 / 06 / 0036), de a hossza nem 8–9 számjegy
// a körzetszámmal együtt. Javaslat: egységes alak, pl. +36 1 234 5678, +36 30 123 4567, +36 99 123 456.
//
// A szerver azonos logikája: worker/core/phone.js (a tests/phone.unit.test.mjs mindkettőt ellenőrzi).
// ═══════════════════════════════════════════════════════════════

export const PHONE_MAX_LEN = 80;
export const PHONE_EXAMPLE = '+36 1 234 5678';
export const PHONE_HINT = 'Pl. +36 1 234 5678 vagy +36 30 123 4567 – csak számjegy, szóköz és + - / ( ) jel.';

const EMPTY = /^[-–—]?$/;
const EXT = /\s*(?:mellék|mell\.|m\.|ext\.?)\s*\d{1,6}\s*$/i;
const ALLOWED = /^[0-9 +\-–/().]+$/;
const MOBILE = new Set(['20', '30', '31', '50', '70']);

/** magyar szám egységes alakja; null, ha nem magyar vagy a hossza nem megfelelő */
export function formatHuPhone(part: string): string | null {
  const raw = String(part || '').trim();
  let d = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) {
    if (!d.startsWith('36')) return null;
    d = d.slice(2);
  } else if (d.startsWith('0036')) d = d.slice(4);
  else if (d.startsWith('06')) d = d.slice(2);
  else if (d.startsWith('36') && (d.length === 10 || d.length === 11)) d = d.slice(2);
  else return null;
  if (d.startsWith('1') && d.length === 8) return `+36 1 ${d.slice(1, 4)} ${d.slice(4)}`;
  if (d.length === 9 && MOBILE.has(d.slice(0, 2))) return `+36 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
  if (d.length === 8 && !d.startsWith('1')) return `+36 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
  return null;
}

const isHu = (part: string) => {
  const raw = String(part).trim();
  const d = raw.replace(/\D/g, '');
  return raw.startsWith('+36') || raw.startsWith('+ 36') || d.startsWith('0036') || /^0\s*6/.test(raw);
};

export interface PhoneCheck { ok: boolean; error?: string; warning?: string; suggestion?: string; value: string }

/** value: a tárolandó (szélein megtisztított) érték */
export function validatePhone(input: unknown): PhoneCheck {
  const value = String(input ?? '').trim().replace(/\s+/g, ' ');
  if (EMPTY.test(value)) return { ok: true, value };
  if (value.length > PHONE_MAX_LEN) return { ok: false, value, error: `Túl hosszú (legfeljebb ${PHONE_MAX_LEN} karakter).` };
  const parts = value.split(/\s*[,;]\s*/).filter((p) => p !== '');
  if (parts.length > 3) return { ok: false, value, error: 'Legfeljebb 3 telefonszám adható meg (vesszővel elválasztva).' };
  const suggestions: string[] = [];
  let warning: string | undefined;
  for (const part of parts) {
    const ext = part.match(EXT);
    const main = ext ? part.slice(0, ext.index ?? part.length).trim() : part;
    if (!main || !ALLOWED.test(main)) {
      return { ok: false, value, error: 'Csak számjegy, szóköz és + - / ( ) jel lehet benne (betű nem).' };
    }
    if (main.indexOf('+') > 0 || (main.match(/\+/g) || []).length > 1) {
      return { ok: false, value, error: 'A „+” jel csak a szám elején állhat.' };
    }
    const digits = main.replace(/\D/g, '').length;
    if (digits < 6) return { ok: false, value, error: 'Túl kevés számjegy (legalább 6).' };
    if (digits > 15) return { ok: false, value, error: 'Túl sok számjegy (legfeljebb 15).' };
    const f = formatHuPhone(main);
    if (!f && isHu(main)) warning = 'A magyar telefonszám a körzetszámmal együtt 8 (mobil: 9) számjegy – ellenőrizd.';
    suggestions.push(f ? `${f}${ext ? ` ${ext[0].trim()}` : ''}` : part);
  }
  const suggestion = suggestions.join(', ');
  return { ok: true, value, ...(warning ? { warning } : {}), ...(suggestion !== value ? { suggestion } : {}) };
}
